import {
  FieldValues,
  TextFieldElement,
  useFormContext,
} from "react-hook-form-mui"
import { Stack, Typography } from "@mui/material"
import { IMailingFormData } from "./mailingForm"
import { InlineCode } from "@/core/components"
import { useAppSelector } from "@/modules/app/hooks"
import { getTelegramState } from "@/modules/telegram/store"
import { useEffect } from "react"
import {
  CHAT_ID_FORMAT_ERROR,
  parseChatIdLike,
} from "@/modules/telegram/constants/mailing"

interface IProps {}
interface IFormData extends Pick<IMailingFormData, "credentials"> {}

const TOKEN_REGEXP = /^\d+:.+$/

/**
 * Валидатор поля credentials. Каждая непустая запись должна иметь формат
 * `bot_token/chat_id`, где chat_id — целое число или @username (см.
 * parseChatIdLike). Иначе показываем точное сообщение и номер строки.
 */
const validateCredentials = (raw: unknown): true | string => {
  if (typeof raw !== "string" || !raw.trim()) {
    return "Укажите хотя бы одну пару bot_token/chat_id"
  }
  const entries = raw
    .split(/[\s,;]+/g)
    .map((s, i) => ({ s: s.trim(), i }))
    .filter(({ s }) => s.length > 0)
  for (const { s, i } of entries) {
    const slashIdx = s.indexOf("/")
    if (slashIdx < 0) {
      return `Запись #${i + 1}: ожидается формат bot_token/chat_id`
    }
    const token = s.slice(0, slashIdx)
    const chatId = s.slice(slashIdx + 1)
    if (!TOKEN_REGEXP.test(token)) {
      return `Запись #${i + 1}: неверный формат токена бота`
    }
    if (parseChatIdLike(chatId) === undefined) {
      return `Запись #${i + 1}: ${CHAT_ID_FORMAT_ERROR}`
    }
  }
  return true
}

export const MailingCredentials = <T extends FieldValues = IFormData>(
  props: IProps,
) => {
  const formContext = useFormContext<IFormData>()
  const { users } = useAppSelector(getTelegramState)

  useEffect(() => {
    formContext.setValue("credentials", users?.join("\n") ?? "")
  }, [users])

  return (
    <Stack spacing={1}>
      <TextFieldElement
        label="Токен бота и ID пользователя"
        name={`credentials`}
        required
        multiline
        rows={20}
        fullWidth
        sx={{ textarea: { resize: "both" } }}
        validation={{ validate: validateCredentials }}
        helperText={
          <Typography variant="caption">
            Формат: <InlineCode>bot_token/chat_id</InlineCode>, где{" "}
            <InlineCode>chat_id</InlineCode> — целое число или username с «@».
            <br />
            Каждая запись с новой строки. Пример:
            <br />
            <InlineCode>
              717232916:AAHPSYtWsicfp0QtD9JO0-fgRuo0qn4Jlow/90876514
            </InlineCode>
            <br />
            <InlineCode>
              717232916:AAHPSYtWsicfp0QtD9JO0-fgRuo0qn4Jlow/@channel_name
            </InlineCode>
          </Typography>
        }
      />
    </Stack>
  )
}
